import type { ImageDefaults } from '@/entities/agent';
import { useCallback, useEffect, useId, useMemo } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  ImageModel,
  ImageQuality,
  ImageResolution,
  ModelFeature,
  ReferenceAccuracy,
  aspectRatioModelsMap,
  IMAGE_MODEL_QUALITIES,
  IMAGE_MODEL_RESOLUTIONS,
  ImageOptions,
  qualityModelsMap,
  QUALITY_OPTIONS,
  referenceAccuracyModelsMap,
  REFERENCE_ACCURACY_OPTIONS,
  resolutionModelsMap,
  SUPPORTED_IMAGE_MODELS,
  AMOUNT_OF_VARIATIONS_OPTIONS,
  customModelIdModelsMap,
} from './ImageGenerationOptions.types';
import { ChatOptionsProps } from '../ChatOptions.types';
import { ChatOptionDropdown } from '../ChatOptionDropdown';
import { ChatPopupNumberInput } from '../ChatPopupNumberInput';
import { useChatStoreInput, useCurrentAgentState } from '@/features/chats/stores';
import { useOptionsManager } from '../hooks/useOptionsManager';
import { ChatPopupTextInput } from '../ChatPopupTextInput';
import { useFilteredModels } from '../hooks/useFilteredModels';
import { useCustomWorkflows } from '@/shared/hooks';
import { CustomWorkflowInlineInput } from '@/entities/custom-workflow';
import { Input, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { AspectRatioIcon, aspectRatiosOf, getOptionsForModel } from './ImageOptionsHelpers';
import { useTranslation } from 'react-i18next';

/**
 * Transforms image options before storing in chat input.
 * Spreads `options` first so sibling keys owned by other components
 * (e.g. `llmOptions` written by the AI Model Selector in ChatInput)
 * are preserved across popup updates.
 */
function toStoreImageOptions(options: ImageOptions): ImageOptions {
  return {
    ...options,
    settings: {
      model: options.settings.model,
      aspectRatio: options.settings.aspectRatio,
      resolution: options.settings.resolution,
      size: options.settings.size,
      referenceAccuracy: options.settings.referenceAccuracy,
      quality: options.settings.quality,
      customModelId: options.settings.customModelId,
      customWorkflowId: options.settings.customWorkflowId,
      customWorkflowInputs: options.settings.customWorkflowInputs,
    },
    variations: options.variations,
    mask: options.mask,
  };
}

/**
 * Reconstructs valid image options from stored data, respecting model-dependent
 * option structure. Spreads `storedOptions` first to retain sibling keys
 * (e.g. `llmOptions`) that this popup doesn't manage but must not erase.
 */
function fromStoreImageOptions(
  storedOptions: Partial<ImageOptions> | undefined,
  defaultOptions: ImageOptions,
): ImageOptions {
  const model = storedOptions?.settings?.model ?? defaultOptions.settings.model;
  // Options can hold other keys (the chat model) before any image setting; until one is stored,
  // the image settings are the defaults, not the model's bare ones, beside what else is stored.
  const base = storedOptions?.settings
    ? storedOptions
    : {
        ...defaultOptions,
        variations: storedOptions?.variations ?? defaultOptions.variations,
        mask: storedOptions?.mask ?? defaultOptions.mask,
      };
  return {
    ...(storedOptions as ImageOptions | undefined),
    ...getOptionsForModel(model, base),
  };
}

/**
 * Set for an agent built in Studio with the Generate image tool, whose card lists the image models
 * and the defaults from its tool node. The Image agent itself leaves them out and reads its own.
 */
export interface ImageGenerationOptionsProps extends ChatOptionsProps {
  models?: readonly string[];
  defaults?: ImageDefaults;
}

export const ImageGenerationOptions = ({
  onOptionUpdated,
  attachmentsCount = 0,
  options: parentOptions,
  models: offeredModels,
  defaults: offeredDefaults,
}: ImageGenerationOptionsProps) => {
  const { t } = useTranslation('chats');

  const { chatInput } = useChatStoreInput();
  const { isDrawingMask } = useCurrentAgentState();

  // A Studio agent's enabled models are its chat models, so its card says which image models apply.
  const agentModels = useFilteredModels(SUPPORTED_IMAGE_MODELS);
  const filteredModels = useMemo(
    () =>
      offeredModels
        ? SUPPORTED_IMAGE_MODELS.filter((model) => offeredModels.includes(model.value))
        : agentModels,
    [offeredModels, agentModels],
  );
  const hasModels = filteredModels.length > 0;

  const { customWorkflows: workflows, isLoading: customWorkflowsLoading } = useCustomWorkflows({
    enabled: !offeredModels,
  });
  // Custom workflows belong to the Image agent's own composer; a Studio agent offers its card's list.
  // A disabled query still returns what the Image agent's composer cached, hence the memo.
  const customWorkflows = useMemo(
    () => (offeredModels ? [] : workflows),
    [offeredModels, workflows],
  );

  const defaultOptions = useMemo(() => {
    if (offeredDefaults) {
      return getOptionsForModel(
        offeredDefaults.settings.model as ImageModel,
        offeredDefaults as Partial<ImageOptions>,
      );
    }
    const firstModel = filteredModels[0]?.value ?? SUPPORTED_IMAGE_MODELS[0].value;
    return getOptionsForModel(firstModel as ImageModel | 'custom-workflow');
  }, [filteredModels, offeredDefaults]);

  const { options, setOptions } = useOptionsManager({
    defaultOptions,
    onOptionUpdated,
    toStore: toStoreImageOptions,
    fromStore: fromStoreImageOptions,
  });

  const id = useId();
  const aspectRatioId = `${id}-aspect-ratio`;
  const resolutionId = `${id}-resolution`;
  const customModelInputId = `${id}-custom-model-id`;
  const variationsId = `${id}-variations`;
  const referenceAccuracyId = `${id}-reference-accuracy`;
  const qualityId = `${id}-quality`;

  const isCustomWorkflow = options.settings.model === 'custom-workflow';
  const isModelWithReferenceAccuracy =
    !isCustomWorkflow && referenceAccuracyModelsMap.includes(options.settings.model as ImageModel);
  const isModelWithAspectRatio =
    !isCustomWorkflow && aspectRatioModelsMap.includes(options.settings.model as ImageModel);
  const isModelWithQuality =
    !isCustomWorkflow && qualityModelsMap.includes(options.settings.model as ImageModel);
  const isModelWithCustomModelId =
    !isCustomWorkflow && customModelIdModelsMap.includes(options.settings.model as ImageModel);
  const isHighResolutionEnabled = useBooleanFlagValue('image_high_resolution', false);
  const isModelWithResolution =
    !isCustomWorkflow && resolutionModelsMap.includes(options.settings.model as ImageModel);
  const shouldShowResolution = isHighResolutionEnabled && isModelWithResolution;
  const resolutionOptions = isModelWithResolution
    ? (IMAGE_MODEL_RESOLUTIONS[options.settings.model as ImageModel] ?? [])
    : [];
  const hasAttachment = attachmentsCount > 0;
  const shouldShowReferenceAccuracy = isModelWithReferenceAccuracy && hasAttachment;
  const shouldShowQuality = isModelWithQuality;
  const qualityOptions = QUALITY_OPTIONS.filter((option) =>
    IMAGE_MODEL_QUALITIES[options.settings.model as ImageModel]?.includes(option.value),
  );
  const shouldShowCustomModelId = isModelWithCustomModelId && !isCustomWorkflow;
  const shouldShowSizeOrAspectRatio = !isCustomWorkflow;
  const shouldShowVariation = !isCustomWorkflow;
  const shouldShowCustomWorkflow = isCustomWorkflow;

  const referencedImage = useMemo(
    () => chatInput.files.find((f) => f.mimeType.startsWith('image/')),
    [chatInput.files],
  );
  const hasReferencedImage = !!referencedImage;

  const dropdownModels = useMemo(() => {
    const models = filteredModels.filter((model) => {
      if (hasReferencedImage && !model.features.includes('edit' as ModelFeature)) return false;
      if (isDrawingMask && !model.features.includes('edit-with-mask' as ModelFeature)) return false;
      return true;
    });
    if (!customWorkflowsLoading && customWorkflows.length > 0) {
      models.push({
        label: t('chats.imageGenerationOptions.customWorkflow'),
        value: 'custom-workflow',
        provider: t('chats.imageGenerationOptions.providerCustom'),
        features: ['create', 'edit'] as readonly ModelFeature[],
      });
    }
    return models.map((model) => ({
      ...model,
      value: model.value as ImageModel | 'custom-workflow',
      secondaryLabel: model.provider,
    }));
  }, [
    filteredModels,
    hasReferencedImage,
    isDrawingMask,
    customWorkflows,
    customWorkflowsLoading,
    t,
  ]);

  // Formatted for the ToggleGroup tabs. Entries with `orientation` get a
  // rectangle icon; entries without it (raw sizes like "1024x1024") render
  // just the label.
  const toggleGroupSizeOrAspectRatioOptions = useMemo(() => {
    return aspectRatiosOf(options.settings.model).map((option) => ({
      ...option,
      icon: option.orientation ? <AspectRatioIcon orientation={option.orientation} /> : undefined,
    }));
  }, [options.settings.model]);

  const dropdownCustomWorkflowOptions = useMemo(
    () =>
      customWorkflows.map((workflow: { id: string; name: string }) => ({
        label: workflow.name,
        value: workflow.id,
      })),
    [customWorkflows],
  );

  const handleReferenceAccuracySelect = useCallback(
    (referenceAccuracy: ReferenceAccuracy) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, referenceAccuracy },
      }));
    },
    [setOptions],
  );

  const handleQualitySelect = useCallback(
    (quality: ImageQuality) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, quality },
      }));
    },
    [setOptions],
  );

  const handleResolutionSelect = useCallback(
    (resolution: ImageResolution) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, resolution },
      }));
    },
    [setOptions],
  );

  const handleCustomModelIdChange = useCallback(
    (customModelId: string) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, customModelId },
      }));
    },
    [setOptions],
  );

  const handleSizeOrAspectRatioSelect = useCallback(
    (value: string) => {
      if (isModelWithAspectRatio) {
        setOptions((prev) => ({
          ...prev,
          settings: { ...prev.settings, aspectRatio: value, size: undefined },
        }));
      } else {
        setOptions((prev) => ({
          ...prev,
          settings: { ...prev.settings, size: value, aspectRatio: undefined },
        }));
      }
    },
    [setOptions, isModelWithAspectRatio],
  );

  const handleVariationsSelect = useCallback(
    (variations: number) => {
      setOptions((prev) => ({ ...prev, variations }));
    },
    [setOptions],
  );

  const handleCustomWorkflowSelect = useCallback(
    (customWorkflowId: string) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, customWorkflowId, customWorkflowInputs: {} },
      }));
    },
    [setOptions],
  );

  const handleCustomWorkflowInputsChange = useCallback(
    (inputs: Record<string, string | number>) => {
      setOptions((prev) => ({
        ...prev,
        settings: { ...prev.settings, customWorkflowInputs: inputs },
      }));
    },
    [setOptions],
  );

  // Validate and fall back the model if not in the allowed list.
  useEffect(() => {
    const isAllowed = dropdownModels.some((m) => m.value === options.settings.model);
    if (!isAllowed && dropdownModels.length > 0) {
      const fallback = dropdownModels[0].value;
      setOptions(getOptionsForModel(fallback));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownModels]);

  // Sync model from the right-side AI Model Selector (lives in ChatInput and
  // writes to options.llmOptions.model). Without this, the popup would show
  // ratios/fields for its own default model instead of the one the user picked
  // in the chip. Reset of dependent fields is delegated to getOptionsForModel.
  const externalSelectedModel = (parentOptions?.llmOptions as { model?: string } | undefined)
    ?.model;
  useEffect(() => {
    if (!externalSelectedModel) return;
    if (externalSelectedModel === options.settings.model) return;
    const isAllowed = dropdownModels.some((m) => m.value === externalSelectedModel);
    if (!isAllowed) return;
    const model = externalSelectedModel as ImageModel | 'custom-workflow';
    setOptions(
      getOptionsForModel(model, { settings: { model, resolution: options.settings.resolution } }),
    );
  }, [
    externalSelectedModel,
    options.settings.model,
    options.settings.resolution,
    dropdownModels,
    setOptions,
  ]);

  // Auto-select the first custom workflow when custom-workflow is selected
  // but no workflow ID is set yet.
  useEffect(() => {
    if (isCustomWorkflow && !options.settings.customWorkflowId && customWorkflows.length > 0) {
      setOptions((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          customWorkflowId: customWorkflows[0].id,
          customWorkflowInputs: {},
        },
      }));
    }
  }, [isCustomWorkflow, options.settings.customWorkflowId, customWorkflows, setOptions]);

  // Clear stale fields when the model no longer supports them.
  useEffect(() => {
    const shouldClearCustomModelId = !shouldShowCustomModelId && options.settings.customModelId;
    const shouldClearReferenceAccuracy =
      !shouldShowReferenceAccuracy && options.settings.referenceAccuracy;
    const shouldClearQuality = !shouldShowQuality && options.settings.quality;
    const shouldClearCustomWorkflow =
      !shouldShowCustomWorkflow && options.settings.customWorkflowId;
    const shouldClearResolution = !isModelWithResolution && options.settings.resolution;

    if (
      shouldClearCustomModelId ||
      shouldClearReferenceAccuracy ||
      shouldClearQuality ||
      shouldClearCustomWorkflow ||
      shouldClearResolution
    ) {
      setOptions((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          ...(shouldClearCustomModelId && { customModelId: undefined }),
          ...(shouldClearReferenceAccuracy && { referenceAccuracy: undefined }),
          ...(shouldClearQuality && { quality: undefined }),
          ...(shouldClearCustomWorkflow && { customWorkflowId: undefined }),
          ...(shouldClearCustomWorkflow && { customWorkflowInputs: undefined }),
          ...(shouldClearResolution && { resolution: undefined }),
        },
      }));
    }
  }, [
    setOptions,
    shouldShowReferenceAccuracy,
    shouldShowQuality,
    shouldShowCustomModelId,
    shouldShowCustomWorkflow,
    isModelWithResolution,
    options.settings.customModelId,
    options.settings.referenceAccuracy,
    options.settings.quality,
    options.settings.customWorkflowId,
    options.settings.resolution,
  ]);

  const currentRatio = isModelWithAspectRatio
    ? options.settings.aspectRatio
    : options.settings.size;

  return (
    <div className="flex flex-col gap-4 w-80">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          {t('chats.imageGenerationOptions.imageAgentSettings')}
        </span>
      </div>

      {shouldShowCustomWorkflow && (
        <>
          <ChatOptionDropdown
            label={t('chats.imageGenerationOptions.customWorkflow')}
            description={t('chats.imageGenerationOptions.selectACustomWorkflowFor')}
            initialValue={options.settings.customWorkflowId ?? ''}
            value={options.settings.customWorkflowId ?? ''}
            onChange={handleCustomWorkflowSelect}
            options={dropdownCustomWorkflowOptions}
            disabled={!hasModels}
          />
          {options.settings.customWorkflowId && (
            <CustomWorkflowInlineInput
              customWorkflowId={options.settings.customWorkflowId}
              data={options.settings.customWorkflowInputs}
              onChange={handleCustomWorkflowInputsChange}
              components={{
                TextInput: ChatPopupTextInput,
                NumberInput: ChatPopupNumberInput,
                OptionDropdown: ChatOptionDropdown,
              }}
            />
          )}
        </>
      )}

      {shouldShowSizeOrAspectRatio && toggleGroupSizeOrAspectRatioOptions.length > 0 && (
        <div className="flex flex-col gap-2">
          <span id={aspectRatioId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.aspectRatio')}
          </span>
          <ToggleGroup
            aria-labelledby={aspectRatioId}
            type="single"
            value={currentRatio ?? ''}
            onValueChange={(value) => {
              if (value) handleSizeOrAspectRatioSelect(value);
            }}
            variant="outline"
            size="sm"
            className="grid grid-cols-4 w-full"
            disabled={!hasModels}
          >
            {toggleGroupSizeOrAspectRatioOptions.map((option) => (
              <ToggleGroupItem
                key={option.value}
                value={option.value}
                className="flex flex-col gap-1 h-auto py-2"
              >
                {option.icon}
                <span className="text-xs">{option.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {shouldShowResolution && resolutionOptions.length > 0 && (
        <div className="flex flex-col gap-2">
          <span id={resolutionId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.resolution')}
          </span>
          <ToggleGroup
            aria-labelledby={resolutionId}
            type="single"
            value={options.settings.resolution ?? '1K'}
            onValueChange={(value) => {
              if (value) handleResolutionSelect(value as ImageResolution);
            }}
            variant="outline"
            size="sm"
            className="grid grid-cols-3 w-full"
            disabled={!hasModels}
          >
            {resolutionOptions.map((tier) => (
              <ToggleGroupItem key={tier} value={tier} className="py-2">
                <span className="text-xs">{tier}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {shouldShowCustomModelId && (
        <div className="flex flex-col gap-2">
          <span id={customModelInputId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.customModelId')}
          </span>
          <Input
            aria-labelledby={customModelInputId}
            size="sm"
            placeholder={t('chats.imageGenerationOptions.optionalCustomModelIdentifierFor')}
            value={options.settings.customModelId ?? ''}
            onChange={(event) => handleCustomModelIdChange(event.target.value)}
            disabled={!hasModels}
          />
        </div>
      )}

      {shouldShowVariation && (
        <div className="flex flex-col gap-2">
          <span id={variationsId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.variations')}
          </span>
          <ToggleGroup
            aria-labelledby={variationsId}
            type="single"
            value={String(options.variations)}
            onValueChange={(value) => {
              if (value) handleVariationsSelect(Number(value));
            }}
            variant="outline"
            size="sm"
            className="grid grid-cols-4 w-full"
            disabled={!hasModels}
          >
            {AMOUNT_OF_VARIATIONS_OPTIONS.map((option) => (
              <ToggleGroupItem key={option.value} value={String(option.value)} className="py-2">
                <span className="text-xs">{option.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {shouldShowReferenceAccuracy && options.settings.referenceAccuracy && (
        <div className="flex flex-col gap-2">
          <span id={referenceAccuracyId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.referenceAccuracy')}
          </span>
          <ToggleGroup
            aria-labelledby={referenceAccuracyId}
            type="single"
            value={options.settings.referenceAccuracy}
            onValueChange={(value) => {
              if (value) handleReferenceAccuracySelect(value as ReferenceAccuracy);
            }}
            variant="outline"
            size="sm"
            className={`grid grid-cols-${REFERENCE_ACCURACY_OPTIONS.length} w-full`}
            disabled={!hasModels}
          >
            {REFERENCE_ACCURACY_OPTIONS.map((option) => (
              <ToggleGroupItem key={option.value} value={option.value} className="py-2">
                <span className="text-xs">{option.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {shouldShowQuality && options.settings.quality && (
        <div className="flex flex-col gap-2">
          <span id={qualityId} className="text-xs font-medium text-foreground-subtle">
            {t('chats.imageGenerationOptions.quality')}
          </span>
          <ToggleGroup
            aria-labelledby={qualityId}
            type="single"
            value={options.settings.quality}
            onValueChange={(value) => {
              if (value) handleQualitySelect(value as ImageQuality);
            }}
            variant="outline"
            size="sm"
            className={`grid ${qualityOptions.length > 4 ? 'grid-cols-3' : 'grid-cols-4'} w-full`}
            disabled={!hasModels}
          >
            {qualityOptions.map((option) => (
              <ToggleGroupItem key={option.value} value={option.value} className="py-2">
                <span className="text-xs">{option.label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}
    </div>
  );
};
